// LEGACY colour + PIN login (being retired, 2026-10-19). Everything that
// belongs to the old method lives in this one file — the colour grid, the
// numpad step, the colour-specific error wording — so retiring it is a
// matter of deleting this file, the GET /login/methods fetch in login.tsx
// and the two colour-only i18n keys. The parent decides whether to render
// it at all (the BE's `colourLogin` flag) and hides its own username form
// while the PIN step is showing.

import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import { readTrustToken } from "../helpers/twoFactorTrust";
import ForgotPasswordButton from "../ForgotPasswordButton";
import LoginLinkButton from "../LoginLinkButton";
import type { LoginResponse } from "../../types/Auth";
import { isNetworkBlocked, loginErrorMessage } from "./loginErrors";
import SecretInput from "./SecretInput";

const colourMap: Record<string, { hex: string; border?: string }> = {
    Blue: { hex: "#3b82f6" },
    Black: { hex: "#1a1a1a" },
    Green: { hex: "#22c55e" },
    Grey: { hex: "#9ca3af" },
    Pink: { hex: "#ec4899" },
    Purple: { hex: "#8b5cf6" },
    Red: { hex: "#ef4444" },
    Yellow: { hex: "#eab308" },
    White: { hex: "#ffffff", border: "#d1d5db" },
};

const colourKeys: Record<string, string> = {
    Blue: "colour_blue", Black: "colour_black", Green: "colour_green",
    Grey: "colour_grey", Pink: "colour_pink", Purple: "colour_purple",
    Red: "colour_red", Yellow: "colour_yellow", White: "colour_white",
};

interface Props {
    /** The shared success path (login.tsx completeLogin). */
    onLoggedIn: (response: LoginResponse) => void;
    /** True while the PIN step is showing — the parent hides the username form. */
    onActiveChange?: (pinStepActive: boolean) => void;
    /** Tighter grid metrics when the grid sits under the username form. */
    compact?: boolean;
    /** Settings › Login screen switch (login.tsx); off = the PIN field is not a password field. */
    allowSavedLogins?: boolean;
}

const ColourPinLogin = ({ onLoggedIn, onActiveChange, compact = false, allowSavedLogins = false }: Props) => {
    const { t } = useTranslation();
    const [loading, set_loading] = useState(false);
    const [selectedColour, set_selectedColour] = useState("");
    const [password, set_password] = useState("");
    const [step, set_step] = useState<"colour" | "pin">("colour");

    // Synchronous guard against a double submit — set/checked before any
    // async work, so it closes the window that the async `loading` state
    // can't (state updates aren't applied until the next render).
    const submittingRef = useRef(false);

    useEffect(() => {
        onActiveChange?.(step === "pin");
    }, [step, onActiveChange]);

    // Track previous step so the right-hand step (pin) animates in from
    // the right when the user moves forward, and the left-hand step
    // (colour) animates back in from the left when they tap the chip
    // to go back. Initial mount is "initial" — no inner animation,
    // since #login-box already runs animate-slideUp.
    const prevStepRef = useRef<"colour" | "pin">(step);
    const stepAnimClass = prevStepRef.current === step
        ? ""
        : prevStepRef.current === "colour"
            ? "login-step-anim login-step-anim--forward"
            : "login-step-anim login-step-anim--backward";
    useEffect(() => {
        prevStepRef.current = step;
    }, [step]);

    const handleColourSelect = (colour: string) => {
        set_selectedColour(colour);
        set_step("pin");
    };

    const handlePasswordButtonClick = (value: string) => {
        if (value === "Reset") {
            set_password("");
            return;
        }
        // Functional update so rapid taps can't accumulate against a stale
        // closure value (and stays capped at 6 digits).
        set_password(prev => (prev.length >= 6 ? prev : prev + value));
    };

    const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
        event.preventDefault();

        // Bail if a submit is already in flight (covers a double tap landing
        // before React re-renders the disabled button).
        if (submittingRef.current) return;

        if (password.length < 6) {
            toast.error(t("server_error"));
            set_password("");
            return;
        }

        submittingRef.current = true;
        set_loading(true);
        try {
            // Same trusted-device token as the identifier form: a colour user
            // who has been individually flagged for two-factor skips the code
            // on a device that passed it within 7 days.
            const trustToken = readTrustToken();
            const res = await apiFetch(`/login/user`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ colour: selectedColour, password, ...(trustToken ? { trustToken } : {}) }),
            });
            const response = await res.json().catch(() => ({} as any));

            if (res.ok) {
                onLoggedIn(response as LoginResponse);
                return;
            }

            set_password("");
            // An allow-list refusal is shown full-screen by App (NetworkBlocked).
            if (!isNetworkBlocked(response)) {
                toast.error(loginErrorMessage(t, res.status, response, "colour"));
            }
        } catch {
            // Network / CORS / cold-start failure: the request never got a
            // response. Surface it and keep the PIN so the user can simply
            // retry without re-entering it.
            toast.error(t("server_error"));
        } finally {
            // Always clears, so the button can never get stuck on "Logging
            // in…". On the success path the component unmounts on redirect,
            // making these no-ops.
            submittingRef.current = false;
            set_loading(false);
        }
    };

    if (step === "colour") {
        return (
            <div className={`login-colour-grid${compact ? " login-colour-grid--compact" : ""} ${stepAnimClass}`.trim()}>
                {Object.entries(colourMap).map(([name, { hex, border }]) => (
                    <button
                        key={name}
                        type="button"
                        className="login-colour-btn"
                        onClick={() => handleColourSelect(name)}
                    >
                        <span
                            className="login-colour-swatch"
                            style={{
                                backgroundColor: hex,
                                border: border ? `2px solid ${border}` : "2px solid transparent",
                            }}
                        />
                        <span className="login-colour-label">{t(colourKeys[name])}</span>
                    </button>
                ))}
            </div>
        );
    }

    return (
        <form className={`login-form ${stepAnimClass}`.trim()} onSubmit={handleSubmit}>
            <button
                type="button"
                className="login-selected-colour"
                onClick={() => { set_step("colour"); set_password(""); }}
            >
                <span
                    className="login-colour-swatch-sm"
                    style={{
                        backgroundColor: colourMap[selectedColour]?.hex,
                        border: colourMap[selectedColour]?.border ? `2px solid ${colourMap[selectedColour].border}` : "2px solid transparent",
                    }}
                />
                <span>{t(colourKeys[selectedColour])}</span>
            </button>

            <div className="login-numbers-group">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                    <button key={n} type="button" onClick={() => handlePasswordButtonClick(String(n))}>{n}</button>
                ))}
                <button type="button" onClick={() => handlePasswordButtonClick("Reset")}>{t("reset")}</button>
                <button type="button" onClick={() => handlePasswordButtonClick("0")}>0</button>
                <button type="button" disabled />
            </div>

            {/* With saving allowed, autoComplete="new-password" is the long-standing
                "don't offer to save a colour PIN" hint — a PIN shared across a colour
                is not a credential a password manager should keep. With it off (the
                default) the field is not a password field at all (SecretInput). */}
            <SecretInput
                allowSave={allowSavedLogins}
                name="password"
                placeholder={t("password")}
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => set_password(e.target.value)}
            />

            <button type="submit" className="btn btn-primary btn-full" disabled={loading || !selectedColour}>
                {loading ? t("logging_in") : t("login")}
            </button>
            <ForgotPasswordButton className="login-forgot-password" />
            <LoginLinkButton
                className="login-forgot-password"
                onLoggedIn={onLoggedIn}
            />
        </form>
    );
};

export default ColourPinLogin;
